import { ErrorHandler, Injectable } from '@angular/core'

import { DataSource } from '@angular/cdk/table'
import { PageEvent } from '@angular/material/paginator'
import { Sort } from '@angular/material/sort'

import { AnnaToimittajatRequest, AnnaToimittajatResponse, ListausLaskuToimittaja } from '@lemon/core-firebase/model/lasku'
import { KayttajaService } from '../_angular/service/kayttaja.service'

import { BehaviorSubject, combineLatest, from, Observable, of as observableOf } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'

import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { lemonShare } from 'app/_jaettu-angular/_rxjs/lemon-share.operator'

export interface ToimittajaSort {
  active: AnnaToimittajatRequest['sort']
  direction: Sort['direction']
}

interface Hakutiedot {
  vapaahaku: string
  start: NumberDate
  end: NumberDate
  sivutustiedot: PageEvent
  lajittelutiedot: ToimittajaSort
}
@Injectable()
export class MaksutToimittajatFirestoreDataSource extends DataSource<ListausLaskuToimittaja> {
  private rivienMaaraSubject = new BehaviorSubject<number>(Number.MAX_SAFE_INTEGER)
  public rivienMaaraObservable: Observable<number> = this.rivienMaaraSubject.asObservable()

  private internalNaytettavatObservable: Observable<ListausLaskuToimittaja[]>
  public naytettavatObservable: Observable<ListausLaskuToimittaja[]>

  private lataaSubject = new BehaviorSubject<boolean>(true)
  public lataaObservable: Observable<boolean> = this.lataaSubject.asObservable()

  private oletussivutustiedot: PageEvent = {
    length: Number.MAX_SAFE_INTEGER,
    pageIndex: 0,
    pageSize: 20,
  }

  private oletuslajittelutiedot: ToimittajaSort = {
    active: 'numero',
    direction: 'asc',
  }

  private oletusHakutiedot: Hakutiedot = {
    vapaahaku: null,
    start: null,
    end: null,
    lajittelutiedot: this.kopioiLajittelutiedot(this.oletuslajittelutiedot),
    sivutustiedot: this.kopioiSivutustiedot(this.oletussivutustiedot),
  }

  private hakutiedotSubject = new BehaviorSubject<Hakutiedot>(this.oletusHakutiedot)
  public hakutiedotObservable = this.hakutiedotSubject.asObservable()

  constructor(
    private errorHandler: ErrorHandler,
    private _kayttajaService: KayttajaService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
  ) {
    super()

    this.internalNaytettavatObservable = combineLatest([this.hakutiedotObservable, this._kayttajaService.kayttajanTiedotObservable]).pipe(
      tap(() => {
        this.lataaSubject.next(true)
      }),
      switchMap(([tiedot, kayttaja]) => {
        if (!kayttaja) {
          this.resetSearchToDefaults()
          return observableOf<ListausLaskuToimittaja[]>([])
        }

        const vapaahaku = tiedot.vapaahaku
        const sort = tiedot.lajittelutiedot
        const pageEvent = tiedot.sivutustiedot

        const limitSize = pageEvent.pageSize
        const offset = pageEvent.pageIndex * limitSize
        const direction = sort.direction === 'asc' ? 'asc' : 'desc'

        const request: AnnaToimittajatRequest = {
          alku: tiedot.start || 100101,
          loppu: tiedot.end || 999999,
          sort: sort.active,
          sortDirection: direction,
          vapaahaku: vapaahaku,
          limit: limitSize,
          offset: offset,
        }
        return from(this._firebaseLemonaid.functionsCall<AnnaToimittajatRequest, AnnaToimittajatResponse>('annaToimittajat', request)).pipe(
          map(res => {
            if (res.e) {
              return []
            }

            this.rivienMaaraSubject.next(res.total)
            return res.toimittajat
          }),
          tap(() => {
            setTimeout(() => {
              this.lataaSubject.next(false)
            }, 0)
          }),
        )
      }),
      lemonShare(),
    )

    this.naytettavatObservable = combineLatest([this.internalNaytettavatObservable, this.lataaObservable]).pipe(
      map(([naytettavat, lataa]) => {
        if (lataa) {
          return []
        }
        return naytettavat
      }),
    )
  }

  getSearch(): string {
    return this.hakutiedotSubject.value.vapaahaku
  }

  getStart(): Date {
    return this._dateService.numberToDate(this.hakutiedotSubject.value.start)
  }

  getEnd(): Date {
    return this._dateService.numberToDate(this.hakutiedotSubject.value.end)
  }

  getPage(): PageEvent {
    return this.hakutiedotSubject.value.sivutustiedot
  }

  getSort(): ToimittajaSort {
    return this.hakutiedotSubject.value.lajittelutiedot
  }

  changePage(pageEvent: PageEvent): void {
    const hakutiedot = this.hakutiedotSubject.value
    hakutiedot.sivutustiedot = pageEvent
    this.hakutiedotSubject.next(hakutiedot)
  }

  changeSort(sort: ToimittajaSort): void {
    const hakutiedot = this.hakutiedotSubject.value
    this.valmistauduHakuehdonMuutokseen(hakutiedot)
    hakutiedot.lajittelutiedot = sort
    this.hakutiedotSubject.next(hakutiedot)
  }

  changeSearch(vapaahaku: string): void {
    const hakutiedot = this.hakutiedotSubject.value
    if (hakutiedot.vapaahaku !== vapaahaku) {
      this.valmistauduHakuehdonMuutokseen(hakutiedot)
      hakutiedot.vapaahaku = vapaahaku
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  changeStart(start: Date): void {
    const hakutiedot = this.hakutiedotSubject.value
    const numberDate = this._dateService.dateToNumber(start)
    if (hakutiedot.start !== numberDate) {
      this.valmistauduHakuehdonMuutokseen(hakutiedot)
      hakutiedot.start = numberDate
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  changeEnd(end: Date): void {
    const hakutiedot = this.hakutiedotSubject.value
    const numberDate = this._dateService.dateToNumber(end)
    if (hakutiedot.end !== numberDate) {
      this.valmistauduHakuehdonMuutokseen(hakutiedot)
      hakutiedot.end = numberDate
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  connect(): Observable<ListausLaskuToimittaja[]> {
    return this.naytettavatObservable
  }

  disconnect() {}

  private kopioiSivutustiedot(lahde: PageEvent): PageEvent {
    return {
      length: lahde.length,
      pageIndex: lahde.pageIndex,
      pageSize: lahde.pageSize,
      previousPageIndex: lahde.previousPageIndex,
    }
  }

  private kopioiLajittelutiedot(lahde: ToimittajaSort): ToimittajaSort {
    return {
      active: lahde.active,
      direction: lahde.direction,
    }
  }

  resetSearchToDefaults() {
    const hakutiedot = this.hakutiedotSubject.value
    const lajittelutiedot = this.kopioiLajittelutiedot(this.oletuslajittelutiedot)
    const sivutustiedot = this.kopioiSivutustiedot(this.oletussivutustiedot)

    // If this is not here, a forever loop is reached when user logs out.
    if (hakutiedot.vapaahaku !== null || JSON.stringify(hakutiedot.lajittelutiedot) !== JSON.stringify(lajittelutiedot) || JSON.stringify(hakutiedot.sivutustiedot) !== JSON.stringify(sivutustiedot)) {
      hakutiedot.vapaahaku = null
      hakutiedot.lajittelutiedot = lajittelutiedot
      hakutiedot.sivutustiedot = sivutustiedot
      // this.lastVisible = []
      this.rivienMaaraSubject.next(Number.MAX_SAFE_INTEGER)
      this.hakutiedotSubject.next(hakutiedot)
    }
  }

  private valmistauduHakuehdonMuutokseen(hakutiedot: Hakutiedot) {
    // this.lastVisible = []
    hakutiedot.sivutustiedot.pageIndex = 0
    delete hakutiedot.sivutustiedot.previousPageIndex
    hakutiedot.sivutustiedot.length = Number.MAX_SAFE_INTEGER
    this.rivienMaaraSubject.next(Number.MAX_SAFE_INTEGER)
  }
}
